'use client';

import { resolveCmsCarouselSlideLabel } from '@/cms/components/blocks/shared/cms-carousel-label';
import { CmsImage } from '@/cms/components/blocks/shared/cms-image';
import { CmsSurface } from '@/cms/components/blocks/shared/cms-surface';
import { cn } from '@/lib/ui/utils';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';

type ReviewStripSlide = {
  author: string;
  quote: string;
  imageSrc?: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
};

type ReviewStripClientProps = {
  blockId: string;
  ariaLabel: string;
  paginationLabel: string;
  slideButtonLabelTemplate?: string | null;
  slides: ReviewStripSlide[];
};

const AUTOPLAY_DELAY_MS = 5000;
const VISIBLE_BACK_CARD_DEPTH = 3;

type ReviewDeckStyle = CSSProperties & {
  '--review-card-offset'?: string;
  '--review-card-y'?: string;
  '--review-card-z'?: string;
  '--review-card-rotate'?: string;
  '--review-card-scale'?: string;
  '--review-card-layer'?: string;
};

type ReviewDeckPlacement = {
  className: string;
  style?: ReviewDeckStyle;
};

const deckDepthStyleMap: Record<
  number,
  {
    offset: string;
    y: string;
    z: string;
    rotate: string;
    scale: string;
    layer: string;
  }
> = {
  1: {
    offset: 'min(12rem, 18cqi)',
    y: 'var(--cms-space-0-72rem)',
    z: '-4rem',
    rotate: '4deg',
    scale: '0.94',
    layer: '7',
  },
  2: {
    offset: 'min(17rem, 24cqi)',
    y: 'var(--cms-space-2rem)',
    z: '-10rem',
    rotate: '7deg',
    scale: '0.84',
    layer: '5',
  },
  3: {
    offset: 'min(21rem, 30cqi)',
    y: 'var(--cms-space-2-5rem)',
    z: '-14rem',
    rotate: '9deg',
    scale: '0.76',
    layer: '3',
  },
};

function resolveSignedDeckDistance(
  index: number,
  activeIndex: number,
  slideCount: number
): number {
  if (index === activeIndex) {
    return 0;
  }

  const forwardDistance = (index - activeIndex + slideCount) % slideCount;
  const backwardDistance = (activeIndex - index + slideCount) % slideCount;

  if (forwardDistance <= backwardDistance) {
    return forwardDistance;
  }

  return backwardDistance * -1;
}

function resolveDeckPlacement(
  index: number,
  activeIndex: number,
  slideCount: number
): ReviewDeckPlacement {
  const signedDistance = resolveSignedDeckDistance(
    index,
    activeIndex,
    slideCount
  );

  if (signedDistance === 0) {
    return {
      className:
        'cms-block-user-reviews-user-reviews-4-user-reviews-4__slideActive',
    };
  }

  const depth = Math.abs(signedDistance);
  if (depth > VISIBLE_BACK_CARD_DEPTH) {
    return {
      className:
        'cms-block-user-reviews-user-reviews-4-user-reviews-4__slideHiddenBack',
    };
  }

  const direction = signedDistance > 0 ? 1 : -1;
  const depthStyle = deckDepthStyleMap[depth];

  return {
    className:
      'cms-block-user-reviews-user-reviews-4-user-reviews-4__slideLayer',
    style: {
      '--review-card-offset':
        direction > 0 ? depthStyle.offset : `calc(-1 * ${depthStyle.offset})`,
      '--review-card-y': depthStyle.y,
      '--review-card-z': depthStyle.z,
      '--review-card-rotate':
        direction > 0 ? depthStyle.rotate : `-${depthStyle.rotate}`,
      '--review-card-scale': depthStyle.scale,
      '--review-card-layer': depthStyle.layer,
    },
  };
}

export function AiEaseReviewStripClient({
  blockId,
  ariaLabel,
  paginationLabel,
  slideButtonLabelTemplate,
  slides,
}: ReviewStripClientProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const slideCount = slides.length;
  const carouselId = useMemo(() => `${blockId}-review-strip`, [blockId]);

  useEffect(() => {
    if (!isAutoPlaying || slideCount <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % slideCount);
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearInterval(timer);
  }, [isAutoPlaying, slideCount]);

  if (slideCount === 0) {
    return null;
  }

  return (
    <section
      id={carouselId}
      className="cms-block-user-reviews-user-reviews-4-user-reviews-4__carousel"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
      onFocusCapture={() => setIsAutoPlaying(false)}
      onMouseEnter={() => setIsAutoPlaying(false)}
    >
      <div className="cms-block-user-reviews-user-reviews-4-user-reviews-4__deck">
        {slides.map((slide, index) => {
          const deckPlacement = resolveDeckPlacement(
            index,
            activeIndex,
            slideCount
          );

          return (
            <CmsSurface
              as="article"
              variant="card"
              className={cn(
                'cms-block-user-reviews-user-reviews-4-user-reviews-4__slide',
                deckPlacement.className
              )}
              key={`${blockId}-${slide.author}-${index}`}
              style={deckPlacement.style}
              aria-hidden={index !== activeIndex}
            >
              {slide.imageSrc ? (
                <div className="cms-block-user-reviews-user-reviews-4-user-reviews-4__media">
                  <CmsImage
                    src={slide.imageSrc}
                    alt={slide.imageAlt}
                    width={slide.imageWidth}
                    height={slide.imageHeight}
                    variant="compact"
                    className="cms-block-user-reviews-user-reviews-4-user-reviews-4__avatar"
                  />
                </div>
              ) : null}
              <div className="cms-block-user-reviews-user-reviews-4-user-reviews-4__body">
                <h3 className="cms-block-user-reviews-user-reviews-4-user-reviews-4__author">
                  {slide.author}
                </h3>
                <p className="cms-block-user-reviews-user-reviews-4-user-reviews-4__quote">
                  {slide.quote}
                </p>
              </div>
            </CmsSurface>
          );
        })}
      </div>

      {slideCount > 1 ? (
        <div
          className="cms-block-user-reviews-user-reviews-4-user-reviews-4__pagination"
          aria-label={paginationLabel}
        >
          {slides.map((slide, index) => (
            <button
              className={cn(
                'cms-block-user-reviews-user-reviews-4-user-reviews-4__bullet',
                index === activeIndex &&
                  'cms-block-user-reviews-user-reviews-4-user-reviews-4__bulletActive'
              )}
              key={`${blockId}-bullet-${index}`}
              type="button"
              aria-label={resolveCmsCarouselSlideLabel(
                slideButtonLabelTemplate,
                index
              )}
              aria-controls={carouselId}
              aria-current={index === activeIndex ? 'true' : undefined}
              onClick={() => {
                setActiveIndex(index);
                setIsAutoPlaying(false);
              }}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
